import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { vorApi } from '../services/api';
import { toast } from 'react-toastify';

const STATUS_COLORS = {
  completed:   { bg: '#D1FAE5', text: '#065F46' },
  in_progress: { bg: '#FEF3C7', text: '#92400E' },
  draft:       { bg: '#E0E7FF', text: '#3730A3' },
  archived:    { bg: '#F3F4F6', text: '#374151' },
};

const fmtCurrency = (val) =>
  val != null ? `$${Number(val).toLocaleString('en-US', { maximumFractionDigits: 0 })}` : '—';

const StatusBadge = ({ status }) => {
  const colors = STATUS_COLORS[status] || STATUS_COLORS.draft;
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 10px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: '600',
      backgroundColor: colors.bg,
      color: colors.text,
    }}>
      {(status || 'draft').replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
    </span>
  );
};

const VorAssessments = () => {
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      setLoading(true);
      const res = await vorApi.listAssessments();
      setAssessments(res.data.assessments || []);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load VoR assessments');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    try {
      await vorApi.deleteAssessment(id);
      toast.success('Assessment deleted');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Delete failed');
    }
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '28px', fontWeight: '700', color: '#1a1a2e' }}>
            Value of Risk Assessments
          </h1>
          <p style={{ margin: '4px 0 0', color: '#6B7280', fontSize: '14px' }}>
            Quantify cybersecurity risk in financial terms
          </p>
        </div>
        <button
          onClick={() => navigate('/vor/assessments/new')}
          style={{
            padding: '10px 20px',
            backgroundColor: '#6B3FA0',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontWeight: '600',
            cursor: 'pointer',
            fontSize: '14px',
          }}
        >
          + New VoR Assessment
        </button>
      </div>

      {/* Table */}
      <div style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: '60px', textAlign: 'center', color: '#6B7280' }}>Loading…</div>
        ) : assessments.length === 0 ? (
          <div style={{ padding: '60px', textAlign: 'center', color: '#6B7280' }}>
            <p style={{ fontSize: '16px', marginBottom: '16px' }}>No VoR assessments yet.</p>
            <button
              onClick={() => navigate('/vor/assessments/new')}
              style={{
                padding: '10px 20px', backgroundColor: '#6B3FA0', color: 'white',
                border: 'none', borderRadius: '6px', fontWeight: '600', cursor: 'pointer',
              }}
            >
              Create Your First Assessment
            </button>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#F3EEFA', borderBottom: '2px solid #E5D5F5' }}>
                {['Client', 'Assessment Name', 'Status', 'Baseline Risk', 'Current Risk', 'Risk Reduction', 'Date', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#4A2B70' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {assessments.map((a, i) => (
                <tr key={a.id} style={{ borderBottom: '1px solid #F3F4F6', backgroundColor: i % 2 === 0 ? 'white' : '#FAFAFA' }}>
                  <td style={{ padding: '12px 16px', fontSize: '14px', color: '#374151' }}>{a.client_name}</td>
                  <td style={{ padding: '12px 16px' }}>
                    <Link to={`/vor/assessments/${a.id}`} style={{ color: '#6B3FA0', fontWeight: '500', textDecoration: 'none' }}>
                      {a.assessment_name}
                    </Link>
                  </td>
                  <td style={{ padding: '12px 16px' }}><StatusBadge status={a.status} /></td>
                  <td style={{ padding: '12px 16px', fontSize: '14px', color: '#374151' }}>{fmtCurrency(a.baseline_risk_value)}</td>
                  <td style={{ padding: '12px 16px', fontSize: '14px', color: '#374151' }}>{fmtCurrency(a.total_current_risk_value)}</td>
                  <td style={{ padding: '12px 16px', fontSize: '14px', color: a.risk_reduction_value > 0 ? '#065F46' : '#374151', fontWeight: a.risk_reduction_value > 0 ? '600' : 'normal' }}>
                    {fmtCurrency(a.risk_reduction_value)}
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: '13px', color: '#6B7280' }}>
                    {a.assessment_date || new Date(a.created_at).toLocaleDateString()}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <Link to={`/vor/assessments/${a.id}`} style={{ color: '#6B3FA0', fontSize: '13px', textDecoration: 'none' }}>View</Link>
                      <span style={{ color: '#D1D5DB' }}>|</span>
                      <Link to={`/vor/assessments/${a.id}/workflow`} style={{ color: '#6B3FA0', fontSize: '13px', textDecoration: 'none' }}>Edit</Link>
                      <span style={{ color: '#D1D5DB' }}>|</span>
                      <button
                        onClick={() => handleDelete(a.id, a.assessment_name)}
                        style={{ background: 'none', border: 'none', color: '#DC2626', fontSize: '13px', cursor: 'pointer', padding: 0 }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default VorAssessments;
